import { store } from '@redux/store';
import { selectModal } from 'layout/redux/selectors';
import modalService from 'layout/services/modalService';
import { userProfileActions } from 'features/userProfile';
import { closeModalSvg } from '../constants/svgIcons';
import { privacyPolicyParagraph } from '../constants/sharedHtml';
import { FIELD_CLASS_NAMES, SELECT_FIELD_CLASS_NAMES } from '../constants/fieldClassNames';
import leadConstructor from '../services/leadConstructor';
import { postLeadInformationForm } from '../services/taskFlow';
import NameInput from './nameInput';
import EmailInput from './emailInput';
import PhoneInput from './phoneInput';
import ZipInput from './zipInput';
import RealtorDropdown from './realtorDropdown';
import ContactMethodDropdown from './contactMethodDropdown';
import LegalText from './legalText';

/**
 * "Get more information" form (NHS20-8178) — reached by clicking "Contact sales center" inside
 * the already-open RequestInformationEntry (NHS20-8176). Reuses the same field/validation/
 * submission machinery InformationFormPaid (Search Results' own "Get more information" screen)
 * already uses, with new nhs-c-input__* presentation via setClassNames() and one extra field
 * (ContactMethodDropdown) InformationFormPaid doesn't have. A dead end per Figma — no back
 * button, only the close (X).
 */
export default class GetMoreInformationForm {
  constructor() {
    const state = store.getState();
    const userProfile = state?.userProfile || {};
    const {
      id, guid, type, communityId, communityName, marketId, marketName, brandName, fromPage,
    } = selectModal(state) ?? {};

    this.id = id;
    this.guid = guid;
    this.type = type;
    this.communityId = communityId;
    this.communityName = communityName;
    this.marketId = marketId;
    this.marketName = marketName;
    this.brandName = brandName;
    this.fromPage = fromPage;
    this.excludeRecommendations = true;
    this.formId = 'getMoreInformationForm';
    this.submitButtonId = 'getMoreInformationSubmitButton';

    this.fullNameInput = new NameInput();
    this.fullNameInput.value = userProfile.name;
    this.fullNameInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('First and last name')
      .setInputId('getMoreInformationName')
      .setErrorMessageId('nhs_ErrorForGetMoreInformationName');

    this.emailAddressInput = new EmailInput();
    this.emailAddressInput.value = userProfile.email;
    this.emailAddressInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('Email address')
      .setInputId('getMoreInformationEmail')
      .setErrorMessageId('nhs_ErrorForGetMoreInformationEmail');

    this.phoneNumberInput = new PhoneInput();
    this.phoneNumberInput.value = userProfile.phone;
    this.phoneNumberInput
      .setClassNames(FIELD_CLASS_NAMES)
      .setLabelText('Phone number')
      .setInputId('getMoreInformationPhone')
      .setErrorMessageId('nhs_ErrorForGetMoreInformationPhone');

    this.postalCodeInput = new ZipInput();
    this.postalCodeInput.value = userProfile.zip;
    this.postalCodeInput.isInternational = userProfile.livesOutside;
    this.postalCodeInput
      .setClassNames({
        ...FIELD_CLASS_NAMES,
        checkboxWrapperClassName: 'nhs-c-input__container--with-text',
        checkboxClassName: 'nhs-c-input__check-box',
        checkboxLabelClassName: '',
      })
      .setLabelText('Your ZIP code')
      .setErrorMessageId('nhs_ErrorForGetMoreInformationZip');
    this.postalCodeInput.removeRequired();

    this.contactMethodDropdown = new ContactMethodDropdown();
    this.contactMethodDropdown.value = userProfile.preferredContactMethod;
    this.contactMethodDropdown.setClassNames(SELECT_FIELD_CLASS_NAMES);

    this.realtorDropdown = new RealtorDropdown();
    this.realtorDropdown.value = userProfile.realtorQuestion;
    this.realtorDropdown
      .setClassNames(SELECT_FIELD_CLASS_NAMES)
      .setInputId('getMoreInformationRealAgent');

    this.legalText = new LegalText(privacyPolicyParagraph, [
      { key: '{0}', value: 'Get more information' },
      { key: '{1}', value: this.brandName ?? 'the builder' },
    ]);
    this.legalText.setId('getMoreInformationLegalText');
    this.legalText.setTextClassName('nhs-o-typography--sans-label-1-extra-small-regular');

    this.onSubmitClick = null;
  }

  /**
   * Gets the form container.
   * @returns {HTMLElement}
   */
  getForm() {
    return document.getElementById(this.formId);
  }

  getSubmitButton() {
    return document.getElementById(this.submitButtonId);
  }

  static closeModal(event) {
    event.preventDefault();
    modalService.clearHtml();
  }

  /**
   * Validates every field, then builds and posts the lead. Mirrors
   * InformationFormPaid.submitInformationForm — same lead type ("information"), different trigger
   * context and field set.
   */
  async submitForm() {
    const fieldValidations = [
      { input: this.fullNameInput, wrapperKey: 'fullName' },
      { input: this.emailAddressInput, wrapperKey: 'email' },
      { input: this.phoneNumberInput, wrapperKey: 'phone' },
      { input: this.postalCodeInput, wrapperKey: 'zip' },
      { input: this.contactMethodDropdown, wrapperKey: 'contactMethod' },
      { input: this.realtorDropdown, wrapperKey: 'realtor' },
    ];

    const submitButton = this.getSubmitButton();
    submitButton.disabled = true;

    const results = await Promise.all(fieldValidations.map(({ input }) => input.validate()));

    fieldValidations.forEach(({ wrapperKey }, index) => {
      const wrapper = this.getForm().querySelector(`[data-field-wrapper="${wrapperKey}"]`);
      wrapper?.classList.toggle('nhs-c-input__wrapper--error', !results[index]);
    });

    if (results.some((isValid) => !isValid)) {
      submitButton.disabled = false;
      return;
    }

    // Only a validated lead is worth persisting for prefill — see bindChangeEvents() for why this
    // isn't dispatched on every field change instead.
    document.body.dispatchEvent(new CustomEvent('user-data-changed', {
      detail: { form: this.getForm() },
    }));

    const formData = new FormData(this.getForm());
    const leadInformation = Object.fromEntries(formData.entries());

    leadInformation.id = this.id;
    leadInformation.type = `${this.type}-information`;
    leadInformation.excludeRecommendations = this.excludeRecommendations;
    leadInformation.fromPage = this.fromPage;
    leadInformation.guid = this.guid;

    const leadObject = leadConstructor(leadInformation);
    leadObject?.setHousingInformation?.({
      communityId: this.communityId,
      communityName: this.communityName,
      marketId: this.marketId,
      marketName: this.marketName,
      brandName: this.brandName,
    });

    postLeadInformationForm(leadObject);
  }

  /**
   * Marks the phone field as required when the user's preferred contact method needs a phone
   * number to act on (Phone or Text Message) — same rule requestTourForm.js applies via
   * ContactRadioGroup, kept consistent here for ContactMethodDropdown.
   * @param {string} value
   */
  setRequiredAttributeInPhone(value) {
    if (value === 'Phone' || value === 'Text Message') {
      this.phoneNumberInput.setRequired();
      return;
    }

    this.phoneNumberInput.removeRequired();
  }

  /**
   * Binds the events needed so the form can respond to user interactions.
   */
  bindEvents() {
    const closeElements = document.querySelectorAll('[data-close]');
    closeElements.forEach((element) => element.addEventListener('click', GetMoreInformationForm.closeModal));

    this.phoneNumberInput.bindEvents();
    this.postalCodeInput.bindEvents();
    this.legalText.bindEvents();
    this.realtorDropdown.setSelectedValue();
    this.contactMethodDropdown.setSelectedValue();

    this.setRequiredAttributeInPhone(this.contactMethodDropdown.value);
    this.contactMethodDropdown.getOnChangeEvent().subscribe(({ preferredContactMethod }) => {
      this.setRequiredAttributeInPhone(preferredContactMethod);
    });

    if (this.fullNameInput.value === '') {
      this.fullNameInput.focus();
    }

    this.onSubmitClick = this.submitForm.bind(this);
    this.getSubmitButton().addEventListener('click', this.onSubmitClick);

    this.bindChangeEvents();
  }

  /**
   * bindChangeEvents: subscribes every input's OnChangeEvent so any field change dispatches its
   * value into userProfileActions.update, matching informationFormPaid.js/brochureFormPaid.js/
   * askQuestionFormPaid.js — this is what the constructor's
   * userProfile.preferredContactMethod/realtorQuestion reads back from on reopen.
   *
   * Deliberately does NOT also persist to the legacy prefillForm module
   * (GlobalResources14/Default/js/modules/prefillForm.js) on every keystroke/blur — that only
   * happens once, in submitForm(), once the lead is actually valid. See submitForm()'s
   * 'user-data-changed' dispatch.
   */
  bindChangeEvents() {
    const changeInputs = [
      this.emailAddressInput,
      this.fullNameInput,
      this.postalCodeInput,
      this.phoneNumberInput,
      this.contactMethodDropdown,
      this.realtorDropdown,
    ];

    changeInputs.forEach((input) => input.getOnChangeEvent().subscribe((data) => {
      store.dispatch(userProfileActions.update(data));
    }));
  }

  /**
   * Removes the bindings that should not be still using.
   */
  unbindEvents() {
    const closeElements = document.querySelectorAll('[data-close]');
    closeElements.forEach((element) => element.removeEventListener('click', GetMoreInformationForm.closeModal));
    this.getSubmitButton()?.removeEventListener('click', this.onSubmitClick);
    this.legalText.unbindEvents();
  }

  /**
   * @returns the HTML that represents the "Get more information" form.
   */
  getHtml() {
    return `
    <div id="${this.formId}-wrapper" data-lead-form>
      <div class="nhs-o-flex-row-items-center nhs-u-gap--200 nhs-u-padding--600">
        <p class="nhs-u-flex--1-0-0 nhs-o-typography--souvenir-heading-1-extra-large-light">Get more information</p>
        <button type="button" class="nhs-n1-c-button__modal-close nhs-n1-c-button__modal-close--transparent" data-close aria-label="Close modal">
          ${closeModalSvg}
        </button>
      </div>
      <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600 nhs-u-padding--0-600-600">
        <p class="nhs-u-width--full nhs-o-typography--sans-body-1-medium-regular">
          Contact the builder to find out more about availability timing, available plans, pricing details, purchase process, and more.
        </p>
        <form id="${this.formId}" class="nhs-u-width--full">
          <input type="hidden" id="leadAction" name="leadAction" value="SendMessageToBuilder" />
          <div class="nhs-o-flex-column-items-start-self-stretch nhs-u-gap--600">
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="fullName">${this.fullNameInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="email">${this.emailAddressInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="phone">${this.phoneNumberInput.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="zip">${this.postalCodeInput.getFieldHtml()}</div>
            ${this.postalCodeInput.getCheckboxHtml()}
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="contactMethod">${this.contactMethodDropdown.getHtml()}</div>
            <div class="nhs-c-input__wrapper nhs-u-gap--300" data-field-wrapper="realtor">${this.realtorDropdown.getHtml()}</div>
          </div>
        </form>
        <button type="button" id="${this.submitButtonId}" class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-lead nhs-u-width--full" data-qa="get-more-information-button">
          Get more information
        </button>
        ${this.legalText.getHtml()}
      </div>
    </div>`;
  }
}
